import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { maasRequiredRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import type { MaasOptions } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import type { ModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import {
  MaasDataDiskMode,
  getDataDiskMode,
  getPrimaryDataVolume,
  syncPrimaryDataVolumeMountPath,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { IMAGE_CONTAINER_DEPLOY_MOUNT_PATH_FIELD } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigFields';
import { buildModelMountPathFromDeployConfig } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigForm';
import styles from '@/delivery-type-plugin/styles/maas-model-step.module.less';

import { hasPresetModelStorage } from './hasPresetModelStorage';
import { MaasModelPersistFields } from './maas-model-persist-fields';
import { MaasStorageSetting } from './maas-storage-setting';
import { ModelCategoryFormItem } from './model-category-field';
import { validateModelFieldsByStorageMode } from './model-source-form-rules';
import { MaasPrimaryDataVolumePersistField } from './primary-data-volume-persist-field';
import type { MaasModelStoragePricingOptions } from './types';

const FormItem = Form.Item;

interface ModelStorageSectionProps extends MaasModelStoragePricingOptions {
  form: FormInstance;
  /** SKU 外层表单，用于数据盘弹窗确认后触发 DataVolume 算价 */
  skuOuterForm?: FormInstance;
  cluster?: string;
  namespace?: string;
  typeOptions?: MaasOptions;
  initValues?: ModelInstanceFormValues;
  fromRepo?: boolean;
  isEdit?: boolean;
  modelInstanceMultiSource?: boolean;
}

/** 模型存储区块：仅维护 _primaryDataVolume 与 model.*，不耦合节点组字段 */
export function ModelStorageSection({
  form,
  skuOuterForm,
  typeOptions,
  initValues,
  fromRepo,
  isEdit,
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  lockActionsWhenFilled,
  resourceCode,
  onPriceChange,
  modelInstanceMultiSource,
}: ModelStorageSectionProps) {
  const { t } = useTranslation();
  const modelSources = Form.useWatch('model.sources', form) as { name?: string }[] | undefined;
  const firstSourceName = modelSources?.[0]?.name;
  const legacyModelName = Form.useWatch('model.name', form) as string | undefined;
  const modelName = modelInstanceMultiSource ? firstSourceName : legacyModelName;
  const mountPathBase = Form.useWatch(IMAGE_CONTAINER_DEPLOY_MOUNT_PATH_FIELD, form) as string | undefined;
  /** 固定规格下单：整块只读；自定义/后付费仅在 SKU 预置已有模型与存储时锁挂载路径 */
  const isOrderFixed = resourceEditMode === 'order-fixed';
  const presetHasDataDisk = useMemo(() => hasPresetModelStorage(initValues as Record<string, unknown> | undefined), [initValues]);
  const mountPathLocked = isOrderFixed || (lockActionsWhenFilled === true && presetHasDataDisk);

  useEffect(() => {
    if (isEdit) return;
    if (!modelName) return;
    const mountPath = buildModelMountPathFromDeployConfig(mountPathBase, modelName);
    if (!mountPath) return;
    form.setFieldValue('model.mountPath', mountPath);
    syncPrimaryDataVolumeMountPath(form, mountPath);
  }, [form, modelName, mountPathBase, isEdit]);

  return (
    <div className={styles.compactForm}>
      <MaasPrimaryDataVolumePersistField />
      <MaasModelPersistFields />
      <FormItem
        field="maasStorageConfigured"
        required
        rules={[
          {
            validator(_, callback) {
              const volume = getPrimaryDataVolume(form);
              if (!volume || !getDataDiskMode(volume)) {
                callback(t('data-center:maas.storage.configure.first'));
                return;
              }
              const mode = getDataDiskMode(volume);
              validateModelFieldsByStorageMode(form, mode === MaasDataDiskMode.Create, callback, {
                multiSource: modelInstanceMultiSource === true,
              });
            },
          },
        ]}
        labelCol={{ span: 0 }}
        wrapperCol={{ span: 24 }}
        style={{ marginBottom: 0 }}
      >
        <MaasStorageSetting
          form={form}
          skuOuterForm={skuOuterForm}
          typeOptions={typeOptions}
          initValues={initValues}
          createStorageOnly={fromRepo}
          isEdit={isEdit}
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          paymentTiming={paymentTiming}
          skuType={skuType}
          resourceEditMode={resourceEditMode}
          lockActionsWhenFilled={lockActionsWhenFilled}
          resourceCode={resourceCode}
          onPriceChange={onPriceChange}
          modelInstanceMultiSource={modelInstanceMultiSource}
        />
      </FormItem>
      {modelInstanceMultiSource || isEdit ? <ModelCategoryFormItem /> : null}
      <Form.Item hidden field={IMAGE_CONTAINER_DEPLOY_MOUNT_PATH_FIELD}>
        <Input />
      </Form.Item>
      <FormItem
        label={t('data-center:maas.model.loadPath')}
        field="model.mountPath"
        rules={maasRequiredRules(t('data-center:maas.model.loadPath'))}
        style={{ marginBottom: 16 }}
      >
        <Input
          placeholder={t('common:input.placeholder')}
          disabled={mountPathLocked}
          onBlur={() => {
            if (mountPathLocked) return;
            const mountPath = String(form.getFieldValue('model.mountPath') || '').trim();
            if (mountPath) {
              syncPrimaryDataVolumeMountPath(form, mountPath);
            }
          }}
        />
      </FormItem>
    </div>
  );
}

export default ModelStorageSection;
